import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import OnboardingLayout from './OnboardingLayout';
import { useProfileState } from '@/contexts/ProfileContext';
import { profile as profileApi } from '@/lib/api';

export default function Step3Review() {
  const navigate = useNavigate();
  const { draft, savedProfile, reload, patchDraft } = useProfileState();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const data = { ...(savedProfile || {}), ...draft };

  useEffect(() => {
    // If user lands here directly with no draft, prefer saved
    if (!data.name && savedProfile?.name) patchDraft(savedProfile);
     
  }, []);

  const launch = async () => {
    setBusy(true); setError(null);
    try {
      await profileApi.save({
        name: data.name || '',
        title: data.title || '',
        location: data.location || '',
        org_type: data.org_type || '',
        career_stage: data.career_stage || '',
        years_in_sustainability: Number(data.years_in_sustainability || 0),
        education_level: data.education_level || '',
        field_of_study: data.field_of_study || '',
        skills_reporting: data.skills_reporting || [],
        skills_analysis: data.skills_analysis || [],
        skills_leadership: data.skills_leadership || [],
        transferable_skills: data.transferable_skills || [],
        academic_skills: data.academic_skills || [],
        additional_skills: data.additional_skills || '',
        pivot_type: data.pivot_type || '',
        hours_per_week: data.hours_per_week || '',
        urgency: data.urgency || '',
        current_situation: data.current_situation || '',
        cv_text: data.cv_text || '',
        cv_source: data.cv_source || '',
        input_type: data.input_type || '',
      });
      await reload();
      navigate('/analysis/loading');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not save profile.');
      setBusy(false);
    }
  };

  const Row = ({ label, value }) => (
    <div className="py-2.5 border-b border-sd-light-gray last:border-0">
      <div className="text-[11px] uppercase tracking-wider text-sd-mid-gray font-medium">{label}</div>
      <div className="text-[14px] text-sd-black font-medium mt-0.5 text-pretty">{value || <span className="text-sd-mid-gray">—</span>}</div>
    </div>
  );

  const allSkills = [
    ...(data.skills_reporting || []),
    ...(data.skills_analysis || []),
    ...(data.skills_leadership || []),
    ...(data.transferable_skills || []),
    ...(data.academic_skills || []),
  ];

  return (
    <OnboardingLayout step={3}>
      <p className="text-[12px] uppercase tracking-[0.14em] text-sd-mid-gray font-medium">Step 4 of 4</p>
      <h2 className="text-[36px] font-semibold text-sd-black mt-2 mb-3 heading-tight">Review & launch</h2>
      <p className="text-sd-mid-gray text-[15px] mb-8">
        Here’s everything we’ll feed into the engine. Looks right?
      </p>

      {error && (
        <div className="rounded-card border-l-4 border-sd-error bg-[#FDF2F2] text-[#76313B] p-4 mb-6 text-[14px]">{error}</div>
      )}

      <div className="sd-card p-6 mb-8">
        <div className="grid md:grid-cols-2 gap-x-8">
          <Row label="Name" value={data.name} />
          <Row label="Job title" value={data.title} />
          <Row label="Location" value={data.location} />
          <Row label="Organisation" value={data.org_type} />
          <Row label="Career stage" value={data.career_stage} />
          <Row label="Years in sustainability / energy" value={String(data.years_in_sustainability ?? '—')} />
          <Row label="Education" value={[data.education_level, data.field_of_study].filter(Boolean).join(' · ')} />
          <Row label="Pivot type" value={data.pivot_type} />
          <Row label="Hours / week" value={data.hours_per_week} />
          <Row label="Urgency" value={data.urgency} />
          <Row label="Current situation" value={data.current_situation} />
          <Row label="Source" value={data.cv_source} />
        </div>
        {allSkills.length > 0 && (
          <div className="mt-5">
            <div className="text-[11px] uppercase tracking-wider text-sd-mid-gray font-medium mb-2">Skills ({allSkills.length})</div>
            <div className="flex flex-wrap gap-1.5">
              {allSkills.map((s, i) => (
                <span key={i} className="text-[12px] font-medium px-2.5 py-1 rounded-pill bg-sd-cream text-sd-dark-gray">{s}</span>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between">
        <button className="sd-btn-secondary" onClick={() => navigate('/onboarding/pivot')} data-testid="review-back" disabled={busy}>← Back</button>
        <button className="sd-btn-primary px-10 py-4 text-[15px]" onClick={launch} disabled={busy} data-testid="review-launch">
          {busy ? 'Saving…' : 'Run My Analysis →'}
        </button>
      </div>
    </OnboardingLayout>
  );
}
